import { BenefitTypeDisplayTextMap } from '@strata/constants';
import { DateFormats, formatDate } from '@strata/helper-functions';
import { FormPageContainer } from '@strata/react-form-header-and-content';
import {
    type ColumnDef,
    type FilterCategory,
    GridWithFilters,
    ProductCell,
    SecondaryData,
} from '@strata/react-grid';
import { InlineMessage } from '@strata/react-inline-message';
import { ServicingRate } from '@strata/react-servicing-rate';
import { type ComponentProps, useEffect } from 'react';

import { type DeclinedReferral, useGetDeclinedReferralsQuery } from 'store/api/performanceApi';
import { useLoggedInSupplierName } from 'store/auth/authThunks';
import { Grids } from 'store/grids/gridsSlice';
import { useGridState } from 'store/grids/gridThunks';

const initialSortProperties: ComponentProps<
    typeof GridWithFilters<DeclinedReferral>
>['sortProperties'] = [{ id: 'declineTimestamp', desc: true }];

const customSearchValueHandlers: ComponentProps<
    typeof GridWithFilters<DeclinedReferral>
>['customSearchValueHandlers'] = {
    declineTimestamp: (value) => formatDate(value, DateFormats.DATE_TIME),
};

const filterCategories: FilterCategory<DeclinedReferral>[] = [
    { key: 'segmentName', title: 'Health Plan' },
    { key: 'product', title: 'Product' },
    {
        key: 'benefit',
        title: 'Benefit',
        valueDisplayTextMap: BenefitTypeDisplayTextMap,
    },
];

const columns: ColumnDef<DeclinedReferral>[] = [
    {
        accessorKey: 'product',
        header: 'Product',
        size: 140,
        cell: ({ row, getValue }) => (
            <ProductCell product={getValue()} isBrand={row.original.isBrand} primary />
        ),
    },
    {
        accessorKey: 'patient',
        header: 'Patient',
        size: 100,
    },
    {
        accessorKey: 'memberDOB',
        header: 'DOB',
        size: 80,
        cell: ({ getValue }) => <SecondaryData data={formatDate(getValue())} />,
    },
    {
        accessorKey: 'offerRate',
        header: 'Offered Rate',
        size: 100,
        cell: ({ row, getValue }) => (
            <SecondaryData
                data={
                    <ServicingRate
                        servicingRate={getValue()}
                        referentPriceType={row.original.referentPriceType}
                    />
                }
            />
        ),
    },
    {
        accessorKey: 'reason',
        header: 'Declined Reason',
        size: 150,
    },
    {
        accessorKey: 'upn',
        header: 'Declined By',
        size: 120,
    },
    {
        accessorKey: 'declineTimestamp',
        header: 'Declined Date',
        size: 100,
        cell: ({ getValue }) => (
            <SecondaryData data={formatDate(getValue(), DateFormats.DATE_TIME)} />
        ),
    },
];

export const DeclinedReferralListPage = () => {
    const loggedInSupplierName = useLoggedInSupplierName();

    const { data, isLoading, refetch } = useGetDeclinedReferralsQuery();
    useEffect(() => {
        void refetch();
    }, [refetch]);

    const {
        sort,
        search,
        activeFilters,
        filterPaneWidth,
        updateGridSort,
        updateGridSearch,
        updateGridActiveFilters,
        updateFilterPaneWidth,
    } = useGridState(Grids.DECLINED_REFERRALS, initialSortProperties);

    return (
        <FormPageContainer>
            <GridWithFilters
                title="Declined Referrals"
                secondaryTitle={loggedInSupplierName}
                headerContent={
                    <div className="margin__bottom--medium">
                        <InlineMessage
                            intent={InlineMessage.Intent.INFO}
                            label="This page contains the most recent 90 days of your organization's Declined Referral activity."
                        />
                    </div>
                }
                data={data}
                isLoading={isLoading}
                filterCategories={filterCategories}
                activeFilters={activeFilters}
                onFilter={updateGridActiveFilters}
                filterPaneRenderWidth={filterPaneWidth}
                onFilterPaneResize={updateFilterPaneWidth}
                sortProperties={sort}
                onSort={updateGridSort}
                enableSearch
                customSearchValueHandlers={customSearchValueHandlers}
                searchText={search}
                onSearch={updateGridSearch}
                columns={columns}
            />
        </FormPageContainer>
    );
};
